import NumericDelta from '@/components/UI/NumericDelta';
import DataTable from '@/components/UI/Table';
import { formatNumber } from '@/helpers';
import { _isNil, _notNil } from '@/littledash';
import { type TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { type FC } from 'react';
import type { Animal } from '@/model/Animal.model';
import type { Study } from '@/model/Study.model';
import type { PresetCalculation } from '@/model/PresetCalculation.model';

interface TableProps {
  study: Study;
  animal: Animal;
}

const Table: FC<TableProps> = ({ study, animal }) => {
  if (_isNil(animal?.latestMeasurement)) {
    return null;
  }

  const {
    settings: { calculations },
  } = study;

  const measurementColumns: Array<TanStackColumnDef<PresetCalculation>> = [
    {
      id: 'name',
      header: 'Measurement',
      accessorKey: 'name',
      enableSorting: true,
    },
    {
      id: 'value',
      header: 'Value',
      accessorFn: (row: PresetCalculation) => {
        const value = animal?.latestMeasurement?.[row.id]?.value;
        return _notNil(value) ? `${formatNumber(value)} ${_notNil(row.unit) ? row.unit : ''}` : '-';
      },
      enableSorting: true,
    },
    {
      id: 'change_from_first',
      header: '% change from first',
      accessorFn: (row: PresetCalculation) => {
        const latestMeasurement = animal?.latestMeasurement?.[row.id];
        return latestMeasurement?.value_change_from_first;
      },
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: PresetCalculation } }) => {
        const latestMeasurement = animal?.latestMeasurement?.[original.id];
        if (_isNil(latestMeasurement?.value_change)) {
          return '-';
        }

        const { value, value_change_from_first: valueChangeFromFirst } = latestMeasurement!;
        return <NumericDelta value={value} valueChange={valueChangeFromFirst} className="f6" />;
      },
    },
    {
      id: 'change_from_last',
      header: '% change from last',
      accessorFn: (row: PresetCalculation) => {
        const latestMeasurement = animal?.latestMeasurement?.[row.id];
        return latestMeasurement?.value_change;
      },
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: PresetCalculation } }) => {
        const latestMeasurement = animal?.latestMeasurement?.[original.id];
        if (_isNil(latestMeasurement?.value_change)) {
          return '-';
        }

        const { value, value_change: valueChange } = latestMeasurement!;
        return <NumericDelta value={value} valueChange={valueChange} className="f6" />;
      },
    },
  ];
  return (
    <>
      <div className="lh-title f4 mb3">Latest Measurements</div>
      <div className="ui-card mb3">
        <div className="overflow-table">
          <DataTable columns={measurementColumns} data={calculations} />
        </div>
      </div>
    </>
  );
};

export default Table;
